import { Link } from 'react-router-dom'
import { useForge } from '../data/useForge'
import { useI18n } from '../i18n/useI18n'
import { Icon } from '../components/Icon'
import { GalleryArt } from '../components/GalleryArt'
import { Layout } from '../components/Layout'

export default function Home() {
  const { t } = useI18n()
  const { registry } = useForge()

  const skillCount = registry?.skills.length ?? 0
  const workflowCount = registry?.workflows.length ?? 0

  return (
    <Layout showInstaller={false}>
      <div className="relative flex flex-col bg-grid-pattern">
        {/* Ambient glow */}
        <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[400px] w-[800px] -translate-x-1/2 rounded-full bg-primary-container/5 blur-[120px]" />

        {/* Hero */}
        <section className="mx-auto flex w-full max-w-container-max flex-col items-center px-4 py-20 text-center md:px-margin-desktop md:py-28">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-outline-variant bg-surface-container-lowest px-3 py-1 shadow-forge-sm">
            <span className="h-2 w-2 animate-pulse rounded-full bg-primary-container" />
            <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
              {t.home.badge}
            </span>
          </div>

          <h1 className="max-w-4xl whitespace-pre-line font-display-lg text-display-lg text-on-surface md:text-[56px]">
            {t.home.title}
          </h1>

          <p className="mt-6 max-w-2xl font-body-lg text-body-lg text-on-surface-variant">
            {t.home.subtitle}
          </p>

          <div className="mt-10 flex w-full flex-col gap-4 sm:w-auto sm:flex-row">
            <Link
              to="/catalog"
              className="inline-flex items-center justify-center gap-2 rounded bg-primary px-8 py-4 font-label-sm text-label-sm font-semibold text-on-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] transition-opacity hover:opacity-90"
            >
              <Icon name="view_comfy_alt" size={18} />
              {t.home.browseSkills}
            </Link>
            <Link
              to="/workflows"
              className="tactile-card inline-flex items-center justify-center gap-2 rounded border border-outline bg-surface-container-lowest px-8 py-4 font-label-sm text-label-sm text-on-surface transition-colors hover:bg-surface-container-low"
            >
              <Icon name="account_tree" size={18} />
              {t.home.exploreWorkflows}
            </Link>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full border-y border-outline-variant bg-surface-container-low py-8">
          <div className="mx-auto flex max-w-container-max flex-wrap items-center justify-center gap-8 px-4 md:gap-16 md:px-margin-desktop">
            <div className="flex flex-col items-center">
              <span className="mb-1 font-headline-lg text-headline-lg text-primary">2+</span>
              <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant">
                {t.home.stats.repos}
              </span>
            </div>
            <div className="hidden h-12 w-px bg-outline-variant md:block" />
            <div className="flex flex-col items-center">
              <span className="mb-1 font-headline-lg text-headline-lg text-primary">{skillCount}</span>
              <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant">
                {t.home.stats.skills}
              </span>
            </div>
            <div className="hidden h-12 w-px bg-outline-variant md:block" />
            <div className="flex flex-col items-center">
              <span className="mb-1 font-headline-lg text-headline-lg text-primary">{workflowCount}</span>
              <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant">
                {t.home.stats.workflows}
              </span>
            </div>
          </div>
        </section>

        {/* Features Bento */}
        <section className="mx-auto w-full max-w-container-max px-4 py-24 md:px-margin-desktop md:py-32">
          <div className="mb-16 text-center">
            <h2 className="font-headline-lg text-headline-lg text-on-surface">{t.home.features.title}</h2>
            <p className="mt-4 font-body-md text-body-md text-on-surface-variant">{t.home.features.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 gap-gutter md:grid-cols-12">
            {/* Skill Catalog large card */}
            <Link
              to="/catalog"
              className="tactile-card group col-span-1 flex h-[400px] flex-col rounded-xl border border-surface-container-high bg-surface-container-lowest p-card-padding md:col-span-8"
            >
              <div className="mb-4 flex items-center gap-3 border-b border-surface-container-high pb-4">
                <Icon name="library_books" size={24} className="text-secondary" />
                <h3 className="font-headline-md text-headline-md text-on-surface">{t.home.features.catalog.title}</h3>
              </div>
              <p className="mb-6 font-body-md text-body-md text-on-surface-variant">{t.home.features.catalog.desc}</p>
              <div className="relative flex flex-1 items-center justify-center overflow-hidden rounded bg-inverse-on-surface">
                <div className="grid w-full max-w-md grid-cols-3 gap-3 p-4">
                  {registry?.skills.slice(0, 6).map((skill) => (
                    <div
                      key={skill.id}
                      className="flex flex-col items-center gap-2 rounded border border-outline-variant bg-surface p-3 transition-transform group-hover:scale-105"
                    >
                      <Icon name={skill.icon} size={20} className="text-primary" />
                      <span className="text-center font-label-sm text-label-sm text-on-surface">{skill.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Link>

            {/* Workflow Templates small card */}
            <Link
              to="/workflows"
              className="tactile-card col-span-1 flex h-[400px] flex-col rounded-xl border border-surface-container-high bg-surface-container-lowest p-card-padding md:col-span-4"
            >
              <div className="mb-4 flex items-center gap-3 border-b border-surface-container-high pb-4">
                <Icon name="schema" size={24} className="text-tertiary" />
                <h3 className="font-headline-md text-headline-md text-on-surface">{t.home.features.workflow.title}</h3>
              </div>
              <p className="mb-6 font-body-md text-body-md text-on-surface-variant">{t.home.features.workflow.desc}</p>
              <div className="flex flex-1 flex-col gap-3">
                {registry?.workflows.slice(0, 3).map((wf) => (
                  <div
                    key={wf.id}
                    className="flex items-center justify-between rounded border border-surface-container-high bg-surface p-3 transition-colors hover:border-outline-variant"
                  >
                    <span className="font-label-sm text-label-sm text-on-surface">{wf.name}</span>
                    <Icon name="arrow_forward" size={16} className="text-outline" />
                  </div>
                ))}
                <div className="flex items-center justify-between rounded border border-surface-container-high bg-surface p-3 opacity-60">
                  <span className="font-label-sm text-label-sm text-on-surface">Citation Networker</span>
                  <Icon name="lock" size={16} className="text-outline" />
                </div>
              </div>
            </Link>

            {/* Install Command Builder large bottom card */}
            <Link
              to="/catalog"
              className="tactile-card group col-span-1 flex h-[350px] flex-col rounded-xl border border-outline bg-[#141413] p-card-padding md:col-span-7"
            >
              <div className="mb-4 flex items-center gap-3 border-b border-[#30312d] pb-4">
                <Icon name="terminal" size={24} className="text-primary-fixed-dim" />
                <h3 className="font-headline-md text-headline-md text-on-primary">{t.home.features.builder.title}</h3>
              </div>
              <div className="flex flex-1 flex-col justify-center px-4">
                <div className="relative overflow-hidden rounded-lg border border-[#30312d] bg-[#1b1c19] p-6 shadow-inner code-active-border">
                  <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-primary-container to-transparent opacity-50" />
                  <pre className="overflow-x-auto font-code-md text-code-md text-surface-container-high">
                    <span className="text-tertiary-fixed-dim">forge</span> install{' '}
                    <span className="text-inverse-primary">--repo</span> academic-core{' '}
                    <span className="text-inverse-primary">--skills</span> "data-synth,lit-parse"{' '}
                    <span className="text-secondary-fixed-dim">--env</span> production
                  </pre>
                </div>
                <p className="mt-6 font-body-md text-body-md text-outline-variant">{t.home.features.builder.desc}</p>
              </div>
            </Link>

            {/* Visual Gallery small bottom card */}
            <Link
              to="/gallery"
              className="tactile-card col-span-1 flex h-[350px] flex-col rounded-xl border border-surface-container-high bg-surface-container-lowest p-card-padding md:col-span-5"
            >
              <div className="mb-4 flex items-center gap-3 border-b border-surface-container-high pb-4">
                <Icon name="image_search" size={24} className="text-secondary" />
                <h3 className="font-headline-md text-headline-md text-on-surface">{t.home.features.gallery.title}</h3>
              </div>
              <p className="mb-6 font-body-md text-body-md text-on-surface-variant">{t.home.features.gallery.desc}</p>
              <div className="flex flex-1 items-center justify-center overflow-hidden rounded border border-surface-dim bg-surface-container">
                <div className="grid grid-cols-2 gap-2 p-4">
                  {registry?.gallery.slice(0, 4).map((item) => (
                    <div
                      key={item.id}
                      className="aspect-square w-24 overflow-hidden rounded border border-outline-variant bg-surface-container-lowest"
                    >
                      <GalleryArt id={item.id} />
                    </div>
                  ))}
                </div>
              </div>
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  )
}
